import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Paper, Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  TextField, MenuItem, Alert, Tooltip, IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UndoIcon from '@mui/icons-material/Undo';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import axios from '../../api/client';
import { AuthContext } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';

// Money moving between the agency's own accounts: cash banked, a wallet loaded from the
// bank, one bank paying another.
//
// No outside party, no income, no expense — except the charge, which is real and is an
// expense. The agency has the same amount of money afterwards; only its location changed.
//
// The pickers offer money accounts and Funds in Transit, and only accounts still in use. A
// retired account is one the agency has stopped using, so offering it would invite a transfer
// into somewhere nothing else can reach.
const emptyForm = {
  fromAccountId: '',
  toAccountId: '',
  amount: '',
  chargeAmount: '',
  entryDate: new Date().toISOString().split('T')[0],
  reference: '',
  narration: ''
};

const ContraEntries = () => {
  const { can } = useContext(AuthContext);
  const { formatMoney } = useCurrency();
  const canReverse = can('contraEntries', 'reverse');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageTotal, setPageTotal] = useState(0);
  const [pageCharges, setPageCharges] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [check, setCheck] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    try {
      const res = await axios.get(`/contra-entries?page=${page + 1}&limit=${rowsPerPage}`);
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
      setPageTotal(res.data.pageTotal || 0);
      setPageCharges(res.data.pageCharges || 0);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load transfers');
    }
  }, [page, rowsPerPage]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get('/contra-entries/accounts');
        setAccounts(Array.isArray(res.data) ? res.data : []);
      } catch (e) { /* the form will say it cannot offer accounts when it opens */ }
    };
    load();
  }, []);

  // Asked of the server so the warning shown is the one it would actually raise, rather than
  // a second copy of the same rules that can drift from them.
  const refreshCheck = useCallback(async (next) => {
    if (!next.fromAccountId || !next.toAccountId || !(parseFloat(next.amount) > 0)) {
      setCheck(null);
      return;
    }
    try {
      const res = await axios.post('/contra-entries/check', {
        fromAccountId: next.fromAccountId,
        toAccountId: next.toAccountId,
        amount: parseFloat(next.amount),
        chargeAmount: parseFloat(next.chargeAmount) || 0
      });
      setCheck(res.data);
    } catch (e) {
      setCheck(null);
    }
  }, []);

  const setField = (field, value) => {
    const next = { ...form, [field]: value };
    setForm(next);
    if (['fromAccountId', 'toAccountId', 'amount', 'chargeAmount'].includes(field)) refreshCheck(next);
  };

  const handleSave = async () => {
    if (!form.fromAccountId || !form.toAccountId) return toast.error('Choose both accounts');
    if (form.fromAccountId === form.toAccountId) return toast.error('The money has to go somewhere else');
    if (!(parseFloat(form.amount) > 0)) return toast.error('Enter an amount greater than zero');
    if (!form.narration.trim()) return toast.error('Give a narration — say why the money moved');

    setSaving(true);
    try {
      const { data } = await axios.post('/contra-entries', {
        ...form,
        amount: parseFloat(form.amount),
        chargeAmount: parseFloat(form.chargeAmount) || 0,
        reference: form.reference || undefined
      });
      toast.success(`Transfer ${data.entryNumber} posted`);
      (data.warnings || []).forEach((w) => toast.warning(w, { autoClose: 10000 }));
      setOpen(false);
      setForm(emptyForm);
      setCheck(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not post this transfer');
    } finally {
      setSaving(false);
    }
  };

  const handleReverse = async (row) => {
    const reason = window.prompt(
      `Reverse ${row.entryNumber} of ${formatMoney(row.amount)}?\n\n`
      + 'This mirrors the journal entry. Nothing is deleted. Give a reason:'
    );
    if (reason === null) return;
    if (!reason.trim()) return toast.error('Give a reason for reversing this transfer');
    try {
      const { data } = await axios.post(`/contra-entries/${row._id}/reverse`, { reason });
      toast.success(data.message);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not reverse this transfer');
    }
  };

  const label = (a) => (a ? `${a.code} ${a.name}` : '—');

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h5">Account Transfers</Typography>
          <Typography variant="body2" color="textSecondary">
            Money moving between the agency&rsquo;s own accounts. The business has the same
            amount afterwards; only its location changes.
          </Typography>
        </Box>
        {can('contraEntries', 'create') && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { setForm(emptyForm); setCheck(null); setOpen(true); }}>
            New Transfer
          </Button>
        )}
      </Box>

      {(pageTotal > 0 || pageCharges > 0) && (
        <Typography variant="body2" sx={{ mb: 2 }} color="textSecondary">
          {formatMoney(pageTotal)} moved on this page
          {pageCharges > 0 && `, ${formatMoney(pageCharges)} paid in charges`}. Reversed
          transfers are excluded.
        </Typography>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Number</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>From</TableCell>
            <TableCell />
            <TableCell>To</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell align="right">Charge</TableCell>
            <TableCell>Narration</TableCell>
            <TableCell>Status</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row._id} hover>
              <TableCell>{row.entryNumber}</TableCell>
              <TableCell>{format(new Date(row.entryDate), 'dd MMM yy')}</TableCell>
              <TableCell>{label(row.fromAccount)}</TableCell>
              <TableCell><ArrowForwardIcon fontSize="small" color="disabled" /></TableCell>
              <TableCell>{label(row.toAccount)}</TableCell>
              <TableCell align="right">{formatMoney(row.amount)}</TableCell>
              <TableCell align="right">
                {parseFloat(row.chargeAmount) > 0 ? formatMoney(row.chargeAmount) : '—'}
              </TableCell>
              <TableCell sx={{ maxWidth: 240 }}>
                <Typography variant="body2" noWrap title={row.narration}>{row.narration}</Typography>
              </TableCell>
              <TableCell>
                {row.status === 'reversed' ? (
                  <Tooltip title={row.reversalReason || 'Reversed'}>
                    <Chip size="small" label="Reversed" variant="outlined" />
                  </Tooltip>
                ) : (
                  <Chip size="small" color="success" label="Posted" variant="outlined" />
                )}
              </TableCell>
              <TableCell align="right">
                {row.status === 'posted' && canReverse && (
                  <Tooltip title="Reverse this transfer">
                    <IconButton size="small" onClick={() => handleReverse(row)}>
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={10}>
                <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                  No transfers recorded.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
      />

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Transfer</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth required label="From" value={form.fromAccountId}
                onChange={(e) => setField('fromAccountId', e.target.value)}
                helperText="The account the money left">
                {accounts.filter((a) => a._id !== form.toAccountId).map((a) => (
                  <MenuItem key={a._id} value={a._id}>{a.code} {a.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth required label="To" value={form.toAccountId}
                onChange={(e) => setField('toAccountId', e.target.value)}
                helperText="The account it went to">
                {accounts.filter((a) => a._id !== form.fromAccountId).map((a) => (
                  <MenuItem key={a._id} value={a._id}>{a.code} {a.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth required type="number" label="Amount" value={form.amount}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setField('amount', e.target.value)}
                helperText="What arrives in the receiving account" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Charge" value={form.chargeAmount}
                onChange={(e) => setField('chargeAmount', e.target.value)}
                helperText="Transfer fee, if any. Posts to Bank Charges." />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth required type="date" label="Date"
                InputLabelProps={{ shrink: true }} value={form.entryDate}
                onChange={(e) => setField('entryDate', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Reference" value={form.reference}
                onChange={(e) => setField('reference', e.target.value)}
                helperText="Slip or transaction number, if there is one" />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth required multiline minRows={2} label="Narration"
                value={form.narration}
                onChange={(e) => setField('narration', e.target.value)}
                helperText="Why the money moved. There is no document behind a transfer, so this is the only record of it." />
            </Grid>

            {check && !check.ok && check.message && (
              <Grid item xs={12}><Alert severity="error">{check.message}</Alert></Grid>
            )}
            {check && check.ok && (check.warnings || []).map((w) => (
              <Grid item xs={12} key={w}><Alert severity="warning">{w}</Alert></Grid>
            ))}

            {parseFloat(form.chargeAmount) > 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  {formatMoney(parseFloat(form.amount) || 0)} arrives and{' '}
                  {formatMoney((parseFloat(form.amount) || 0) + (parseFloat(form.chargeAmount) || 0))}{' '}
                  leaves. The charge is an expense, not part of the transfer.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={saving || (check && !check.ok)}>
            {saving ? 'Posting…' : 'Post Transfer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ContraEntries;
